import { History, Pencil, Check, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FormField } from "@babackapp/ui/components/form";
import { Switch } from "@babackapp/ui/components/switch";
import { Button } from "@babackapp/ui/components/button";
import { cn } from "@babackapp/ui/lib/utils";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import { useWorkflowsEditorConfig } from "../context/workflows-editor-config-context";
import {
  useWorkflowForm,
  useWorkflowsEditorWorkflows,
} from "../context/workflows-editor-workflows-context";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { nodeColorToTailwindClasses } from "../lib/node-color-to-tailwind-classes";
import { useTriggerLabel } from "../listing/use-trigger-label";

interface WorkflowTopBarProps {
  editMode: boolean;
  onToggleEdit: () => void;
  executionsOpen: boolean;
  onToggleExecutions: () => void;
}

export function WorkflowTopBar({
  editMode,
  onToggleEdit,
  executionsOpen,
  onToggleExecutions,
}: WorkflowTopBarProps) {
  const { t } = useTranslation("trackoffice");
  const { control, watch } = useWorkflowForm();
  const { currentWorkflow } = useWorkflowsEditorWorkflows();
  const { data: shop } = useCurrentShopSuspense();
  const { api } = useWorkflowsEditorConfig();
  const getTriggerLabel = useTriggerLabel();

  const { data: executions } = api.useWorkflowExecutions(
    shop.id,
    currentWorkflow?.id || "",
  );
  const totalCount = executions?.totalCount ?? 0;

  // Watch the form trigger so the label updates live when the trigger changes
  const trigger = watch("workflow.trigger");
  const triggerLabel = trigger ? getTriggerLabel(trigger) : null;

  return (
    <div className="z-[50] flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {triggerLabel && (
          <span
            className={cn(
              "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[12.5px] font-semibold",
              nodeColorToTailwindClasses(triggerLabel.accent).background,
              nodeColorToTailwindClasses(triggerLabel.accent).text,
            )}
          >
            <Play className="size-3 fill-current" />
            {triggerLabel.summary ?? triggerLabel.typeLabel}
          </span>
        )}
        <FormField
          control={control}
          name="workflow.name"
          render={({ field }) => (
            <FormInput
              label=""
              value={field.value as string}
              onChange={field.onChange}
              placeholder={t(
                "workflowsEditor.header.workflowNamePlaceholder",
                "Add a title to identify this workflow...",
              )}
              className="h-8 w-auto min-w-0 max-w-xs border-transparent bg-transparent px-1 text-sm text-muted-foreground shadow-none hover:border-input focus:border-input"
            />
          )}
        />
      </div>

      <Button
        variant={executionsOpen ? "secondary" : "outline"}
        size="sm"
        className="gap-1"
        onClick={onToggleExecutions}
      >
        <History className="size-3.5" />
        {t("workflowsEditor.workflowExecutions", "Executions")}
        <span className="text-muted-foreground">{totalCount}</span>
      </Button>

      <Button
        variant={editMode ? "secondary" : "default"}
        size="sm"
        className="gap-1"
        onClick={onToggleEdit}
      >
        {editMode ? (
          <>
            <Check className="size-3.5" />
            {t("workflowsEditor.header.doneEditing", "Done")}
          </>
        ) : (
          <>
            <Pencil className="size-3.5" />
            {t("workflowsEditor.header.edit", "Edit")}
          </>
        )}
      </Button>

      <FormField
        control={control}
        name="workflow.isActive"
        render={({ field }) => (
          <label
            htmlFor="workflow-toggle"
            className="flex shrink-0 cursor-pointer items-center gap-2"
          >
            <span
              className={cn(
                "text-sm font-medium",
                field.value ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {field.value
                ? t("workflowsEditor.header.active", "Active")
                : t("workflowsEditor.header.inactive", "Inactive")}
            </span>
            <Switch
              id="workflow-toggle"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          </label>
        )}
      />
    </div>
  );
}
